/**
 * lib/incomingInspectionTolerance.js
 * Dimension / Tolerance parsing + pass/fail evaluation — ported 1:1 from
 * the standalone tool's assets/js/app.js (splitDimensionCell(),
 * parseToleranceOffset(), rowStatus()) so the browser preview here matches
 * exactly what backend/incoming_inspection.py computes and stores.
 *
 * The Inspection Criteria Report writes each spec as one combined cell,
 * e.g. "36 +/-0.05" or "20.5 +0.1/-0.00". splitDimensionCell() splits that
 * into a Dimension field (nominal, "36") and a Tolerance field (offset
 * only, "+/-0.05"). Non-numeric callouts (thread specs etc.) can't be
 * split — the whole string is kept as the dimension, tolerance blank.
 */

export function splitDimensionCell(text) {
  const t = String(text ?? '').trim();
  let m = t.match(/^(-?\d+(?:\.\d+)?)\s*(\+\d+(?:\.\d+)?\s*\/\s*-\d+(?:\.\d+)?)$/);
  if (m) return { dimension: m[1], tolerance: m[2].replace(/\s+/g, '') };
  m = t.match(/^(-?\d+(?:\.\d+)?)\s*(\+\/-\s*\d+(?:\.\d+)?)$/);
  if (m) return { dimension: m[1], tolerance: m[2].replace(/\s+/g, '') };
  return { dimension: t, tolerance: '' };
}

/**
 * A dimension typed straight into the Dimension box sometimes carries its
 * tolerance along with it, e.g. "A/F44+/-0.10" (an Across-Flats callout) —
 * the user has nowhere else to put the tolerance yet since it's all one
 * string. If the Dimension field ends in a recognizable tolerance suffix
 * and the Tolerance field is still empty, split it off into its own field
 * instead of leaving the whole thing stuck un-evaluable in Dimension.
 * Returns { dimension, tolerance } or null if there's no trailing
 * tolerance to split off.
 */
export function splitTrailingTolerance(text) {
  const t = String(text ?? '').trim();
  let m = t.match(/^(.*\S)\s*(\+\d+(?:\.\d+)?\s*\/\s*-\d+(?:\.\d+)?)$/);
  if (m) return { dimension: m[1].trim(), tolerance: m[2].replace(/\s+/g, '') };
  m = t.match(/^(.*\S)\s*(\+\/-\s*\d+(?:\.\d+)?)$/);
  if (m) return { dimension: m[1].trim(), tolerance: m[2].replace(/\s+/g, '') };
  return null;
}

/**
 * Extracts the numeric nominal value from a Dimension field for the
 * Min/Max + ACCEPTED/NC computation below. Handles both a plain number
 * ("44") and a number with a short code prefix ("A/F44", "AF 44", "R12.5")
 * — but NOT a callout with digits in more than one place (e.g.
 * "M10X1.5", "1 1/2'' BSP"), which has no single unambiguous nominal and
 * is left un-evaluable (PENDING), same as the original tool.
 */
export function extractNominal(dimensionText) {
  const t = String(dimensionText ?? '').trim();
  if (t === '') return null;
  if (/^-?\d+(?:\.\d+)?$/.test(t)) return parseFloat(t);
  const m = t.match(/^[A-Za-z/.\s-]*?(-?\d+(?:\.\d+)?)$/);
  return m ? parseFloat(m[1]) : null;
}

/** Parses a tolerance-OFFSET-only string (no nominal), e.g. "+/-0.05" or
 *  "+0.1/-0.00", into { plus, minus } or null. */
export function parseToleranceOffset(text) {
  if (!text) return null;
  const t = String(text).trim();
  let m = t.match(/^\+(\d+(?:\.\d+)?)\/-(\d+(?:\.\d+)?)$/);
  if (m) return { plus: parseFloat(m[1]), minus: parseFloat(m[2]) };
  m = t.match(/^\+\/-\s*(\d+(?:\.\d+)?)$/);
  if (m) { const v = parseFloat(m[1]); return { plus: v, minus: v }; }
  return null;
}

/** ACCEPTED/NC/PENDING verdict for one dimension row: { dimension, tolerance, samples }. */
export function rowStatus(row) {
  const nominal = extractNominal(row.dimension);
  if (nominal === null || isNaN(nominal)) return 'PENDING';
  const offset = parseToleranceOffset(row.tolerance);
  const filled = (row.samples || []).filter((v) => v !== '' && v !== null && v !== undefined && !isNaN(v)).map(Number);
  if (!offset || filled.length === 0) return 'PENDING';
  const min = nominal - offset.minus, max = nominal + offset.plus;
  const allWithin = filled.every((v) => v >= min && v <= max);
  return allWithin ? 'ACCEPTED' : 'NC';
}

/** Auto-computed Min/Max limits from a row's Dimension (nominal) + Tolerance (offset). */
export function computeRowLimits(row) {
  const nominal = extractNominal(row.dimension);
  if (nominal === null || isNaN(nominal)) return null;
  const offset = parseToleranceOffset(row.tolerance);
  if (!offset) return null;
  return { min: nominal - offset.minus, max: nominal + offset.plus };
}

export function fmtLimit(v) {
  return v === null || v === undefined || isNaN(v) ? '—' : v.toFixed(4).replace(/\.?0+$/, '');
}
